import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import LearningPathAgentV2 from "../AgentResponse/LearningPathAgentV2";
import axiosClient from "../../api/AxiosClient";
import styles from "./chatwindow.module.css";

// Type definition for old training search API response (legacy)
type TrainingSearchResponse = {
  type: string;
  agent_id: string;
  workflow_id: string;
  created_at: string;
  result: {
    data: {
      summary: string;
      total_duration: string;
      phases: any[];
      timeline_fit: string;
      key_milestones: string[];
      notes?: string;
    };
  };
};

// Type definition for new training search API response
type LearningPath = {
  learning_path_id: string;
  learning_path_name: string;
  description: string;
  skills_covered: string[];
  type: string;
  source: string[];
  link: string[] | null;
  duration_display: string | null;
};

type TrainingSearchResponseV2 = {
  status: string;
  learning_paths: LearningPath[];
};

export default function TrainingSearchButton(): JSX.Element {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trainingData, setTrainingData] = useState<any>(null);
  const [noDataAvailable, setNoDataAvailable] = useState(false);

  const handleSearchTraining = async () => {
    setLoading(true);
    setError(null);
    setNoDataAvailable(false);

    try {
      const response = await axiosClient.get<TrainingSearchResponseV2>(
        "/training-search/learning-paths",
      );

      // Check if response has valid data
      if (
        !response.data ||
        !response.data.learning_paths ||
        response.data.learning_paths.length === 0
      ) {
        setNoDataAvailable(true);
        setTrainingData(null);
      } else {
        setTrainingData(response.data);
      }
    } catch (err: any) {
      console.error("Error fetching training search data:", err);

      // Check if it's a 404 or no data error
      if (
        err.response?.status === 404 ||
        err.response?.data?.message?.includes("not found")
      ) {
        setNoDataAvailable(true);
      } else {
        setError(
          err.response?.data?.message ||
            "Failed to fetch training search data. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setTrainingData(null);
    setError(null);
  };

  const handleNavigateToAssessment = () => {
    navigate("/skill-gap-assessment");
  };

  return (
    <Box className={styles.learningPathContainer}>
      {/* Error display */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 2, mx: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* No data available message */}
      {noDataAvailable && (
        <Box className={styles.learningPathNoDataWrapper}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Please complete the skill gap assessment and return here to
              search for training opportunities.
            </Typography>
          </Alert>
          <Box className={styles.learningPathButtonWrapper}>
            <Button
              variant="contained"
              onClick={handleNavigateToAssessment}
              className={styles.learningPathButton}
            >
              Go to Skill Gap Assessment
            </Button>
          </Box>
        </Box>
      )}

      {/* Search button - shown when no training data is loaded */}
      {!trainingData && !noDataAvailable && (
        <Box className={styles.learningPathButtonWrapper}>
          <Button
            variant="contained"
            onClick={handleSearchTraining}
            disabled={loading}
            className={styles.learningPathButton}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: "#004141" }} />
                Loading Training Data...
              </>
            ) : (
              "Search Training Opportunities"
            )}
          </Button>
        </Box>
      )}

      {/* Results display - shown after data is fetched, using LearningPathAgentV2 */}
      {trainingData && (
        <Box className={styles.learningPathResults}>
          <LearningPathAgentV2 parsedContent={trainingData} />
        </Box>
      )}
    </Box>
  );
}
